import { IntrinsicSizeImg } from 'components/Img';
import { LayoutContainer } from 'components/Layout';
import { OptionalLink } from 'components/Link';
import { useIsCompanySite } from 'hooks';
import type { ImageColumn } from 'models/sitecore';
import type { HTMLString } from 'types';
import { cn } from 'utils/classNames';

import { flattenImageColumns } from './helpers';
import PageFooterInfo from './PageFooterInfo';

interface Props {
	columns: ImageColumn[] | undefined;
	infoText?: HTMLString;
}

/** Columns of small images, possibly linked. */
export default function PageFooterImages({ columns, infoText }: Props) {
	const visibleColumns = flattenImageColumns(columns);
	const colCount = visibleColumns.length;
	const isCompanySite = useIsCompanySite();

	if (colCount === 0) {
		return null;
	}

	return (
		<LayoutContainer
			outerClassName={cn(
				'py-10 md:border-t md:py-6',
				isCompanySite
					? 'md:border-t-opacityLighten'
					: 'md:border-t-brandDecorative',
			)}
			withGrid
		>
			<PageFooterInfo
				text={infoText}
				className="col-span-full max-md:max-w-120 md:col-span-5 lg:hidden"
			/>

			<div className="col-span-full md:col-span-6 md:col-start-7 lg:col-span-full lg:flex lg:gap-6">
				{visibleColumns.map((col) => (
					<section
						key={col.title}
						className="mt-10 md:first:mt-0 lg:mt-0 lg:flex-1"
					>
						<h3 className="mb-4 font-body text-lg font-bold">{col.title}</h3>
						<p className="flex flex-wrap gap-3">
							{col.images.map((img) => (
								<OptionalLink
									key={img.src}
									href={img.href}
									fallback="span"
									target={img.target}
									className={cn(
										'inline-block min-w-4 shrink-0 rounded-xs',
										img.padding && 'bg-base p-2',
										!img.large && 'h-10',
										img.large && 'h-20',
									)}
									linkClassName="hover:outline-1 hover:outline-offset-2 hover:outline-solid"
								>
									<IntrinsicSizeImg
										src={img.src}
										alt={img.alt || img.text}
										className="h-full"
									/>
								</OptionalLink>
							))}
						</p>
					</section>
				))}
			</div>
		</LayoutContainer>
	);
}
PageFooterImages.displayName = 'PageFooterImages';
